import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LinkBuilding = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [backlinks, setBacklinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [newBacklink, setNewBacklink] = useState({ source_url: '', source_domain: '', target_url: 'https://fortworthdental.com', anchor_text: '', category: 'general', domain_authority: '' });
  
  // Automation states
  const [outreachOpps, setOutreachOpps] = useState([]);
  const [healthReport, setHealthReport] = useState(null);
  const [prOutlets, setPrOutlets] = useState([]);
  const [pressReleases, setPressReleases] = useState([]);
  const [sendingOutreach, setSendingOutreach] = useState({});
  const [checkingHealth, setCheckingHealth] = useState(false);
  const [sendingPR, setSendingPR] = useState(false);
  const [selectedPR, setSelectedPR] = useState('');
  const [selectedOutlets, setSelectedOutlets] = useState([]);

  // Initial data fetch on mount
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        const [dashRes, oppRes, blRes] = await Promise.all([
          fetch(`${API_URL}/api/link-building/dashboard`),
          fetch(`${API_URL}/api/link-building/opportunities`),
          fetch(`${API_URL}/api/link-building/backlinks`)
        ]);
        if (dashRes.ok) setDashboard(await dashRes.json());
        if (oppRes.ok) setOpportunities((await oppRes.json()).opportunities || []);
        if (blRes.ok) setBacklinks((await blRes.json()).backlinks || []);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    loadInitialData();
  }, []);

  // Tab-specific data fetching
  useEffect(() => {
    if (activeTab === 'outreach') {
      const loadOutreachData = async () => {
        try {
          const res = await fetch(`${API_URL}/api/link-building/outreach/opportunities`);
          if (res.ok) setOutreachOpps((await res.json()).opportunities || []);
        } catch (err) { console.error(err); }
      };
      loadOutreachData();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'health') {
      const loadHealthReport = async () => {
        try {
          const res = await fetch(`${API_URL}/api/link-building/backlinks/health-report`);
          if (res.ok) setHealthReport(await res.json());
        } catch (err) { console.error(err); }
      };
      loadHealthReport();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'pr-integration') {
      const loadPRData = async () => {
        try {
          const [outletsRes, prRes] = await Promise.all([
            fetch(`${API_URL}/api/link-building/pr-outlets`),
            fetch(`${API_URL}/api/press-releases?status=published`)
          ]);
          if (outletsRes.ok) setPrOutlets((await outletsRes.json()).outlets || []);
          if (prRes.ok) setPressReleases((await prRes.json()).press_releases || []);
        } catch (err) { console.error(err); }
      };
      loadPRData();
    }
  }, [activeTab]);

  // Refetch functions for manual refresh
  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashRes, oppRes, blRes] = await Promise.all([
        fetch(`${API_URL}/api/link-building/dashboard`),
        fetch(`${API_URL}/api/link-building/opportunities`),
        fetch(`${API_URL}/api/link-building/backlinks`)
      ]);
      if (dashRes.ok) setDashboard(await dashRes.json());
      if (oppRes.ok) setOpportunities((await oppRes.json()).opportunities || []);
      if (blRes.ok) setBacklinks((await blRes.json()).backlinks || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fetchOutreachData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/link-building/outreach/opportunities`);
      if (res.ok) setOutreachOpps((await res.json()).opportunities || []);
    } catch (err) { console.error(err); }
  };

  const fetchHealthReport = async () => {
    try {
      const res = await fetch(`${API_URL}/api/link-building/backlinks/health-report`);
      if (res.ok) setHealthReport(await res.json());
    } catch (err) { console.error(err); }
  };

  const updateOpportunityStatus = async (id, status) => {
    await fetch(`${API_URL}/api/link-building/opportunities/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    });
    fetchData();
  };

  const addBacklink = async () => {
    await fetch(`${API_URL}/api/link-building/backlinks`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...newBacklink, domain_authority: parseInt(newBacklink.domain_authority) || null })
    });
    setNewBacklink({ source_url: '', source_domain: '', target_url: 'https://fortworthdental.com', anchor_text: '', category: 'general', domain_authority: '' });
    fetchData();
  };

  // Automation Functions
  const sendOutreach = async (oppName) => {
    setSendingOutreach(prev => ({ ...prev, [oppName]: true }));
    try {
      const res = await fetch(`${API_URL}/api/link-building/outreach/send/${encodeURIComponent(oppName)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact_name: 'Team' })
      });
      if (res.ok) {
        alert('Outreach email sent successfully!');
        fetchOutreachData();
      } else {
        const err = await res.json();
        alert(`Failed: ${err.detail || 'Unknown error'}`);
      }
    } catch (err) { alert('Error sending outreach'); }
    setSendingOutreach(prev => ({ ...prev, [oppName]: false }));
  };

  const sendBulkOutreach = async () => {
    const notContacted = outreachOpps.filter(o => !o.outreach_sent).map(o => o.name);
    if (notContacted.length === 0) { alert('All opportunities already contacted!'); return; }
    if (!window.confirm(`Send outreach to ${notContacted.length} opportunities?`)) return;
    
    setSendingOutreach({ bulk: true });
    try {
      const res = await fetch(`${API_URL}/api/link-building/outreach/send-bulk`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ opportunity_names: notContacted, contact_name: 'Team' })
      });
      if (res.ok) {
        const data = await res.json();
        alert(`Sent ${data.sent} emails, ${data.failed} failed`);
        fetchOutreachData();
      }
    } catch (err) { alert('Error sending bulk outreach'); }
    setSendingOutreach({});
  };

  const checkAllHealth = async () => {
    setCheckingHealth(true);
    try {
      const res = await fetch(`${API_URL}/api/link-building/backlinks/check-all`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        alert(`Checked ${data.results.checked} backlinks. Active: ${data.results.active}, Lost: ${data.results.lost}`);
        fetchHealthReport();
        fetchData();
      }
    } catch (err) { alert('Error checking backlinks'); }
    setCheckingHealth(false);
  };

  const sendPRToOutlets = async () => {
    if (!selectedPR) { alert('Please select a press release'); return; }
    if (selectedOutlets.length === 0) { alert('Please select at least one outlet'); return; }
    
    setSendingPR(true);
    try {
      const res = await fetch(`${API_URL}/api/link-building/send-pr-to-outlets`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ press_release_id: selectedPR, outlet_names: selectedOutlets })
      });
      if (res.ok) {
        const data = await res.json();
        alert(`Sent to ${data.sent} outlets, ${data.failed} failed`);
        setSelectedPR('');
        setSelectedOutlets([]);
      }
    } catch (err) { alert('Error sending PR'); }
    setSendingPR(false);
  };

  const toggleOutlet = (name) => {
    setSelectedOutlets(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
  };

  const getCategoryIcon = (cat) => {
    const icons = { 'local-news': '📰', 'chamber': '🏛️', 'local-business': '🏢', 'healthcare': '🏥', 'dental-associations': '🦷', 'sponsorship': '🎗️', 'community': '🤝', 'education': '🎓', 'sports': '⚽', 'charity': '❤️' };
    return icons[cat] || '🔗';
  };

  const getStatusBadge = (status) => {
    const styles = { identified: 'bg-gray-100 text-gray-600', contacted: 'bg-blue-100 text-blue-700', 'in-progress': 'bg-yellow-100 text-yellow-700', acquired: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700' };
    return <span className={`px-2 py-1 rounded text-xs font-medium ${styles[status] || styles.identified}`}>{status}</span>;
  };

  const getPriorityBadge = (p) => {
    if (p >= 5) return <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700 font-bold">Critical</span>;
    if (p >= 4) return <span className="px-2 py-0.5 rounded text-xs bg-orange-100 text-orange-700">High</span>;
    if (p >= 3) return <span className="px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-700">Medium</span>;
    return <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">Low</span>;
  };

  const filteredOpportunities = selectedCategory === 'all' ? opportunities : opportunities.filter(o => o.category === selectedCategory);
  const categories = [...new Set(opportunities.map(o => o.category))];

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'citation-workflow', label: 'Citation Workflow', icon: '🚀' },
    { id: 'opportunities', label: 'Opportunities', icon: '🎯' },
    { id: 'backlinks', label: 'Backlinks', icon: '🔗' },
    { id: 'outreach', label: 'Auto-Outreach', icon: '📧' },
    { id: 'health', label: 'Health Monitor', icon: '🔍' },
    { id: 'pr-integration', label: 'PR → News', icon: '📰' },
    { id: 'add', label: 'Add Backlink', icon: '➕' },
  ];

  // Citation Workflow states
  const [citationDashboard, setCitationDashboard] = useState(null);
  const [citationTasks, setCitationTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskGuide, setTaskGuide] = useState(null);
  const [submissionContent, setSubmissionContent] = useState(null);
  const [copied, setCopied] = useState('');

  const fetchCitationData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/link-building/citation-automation/dashboard`);
      if (res.ok) {
        const data = await res.json();
        setCitationDashboard(data);
        setCitationTasks(data.all_tasks || []);
      }
    } catch (err) { console.error(err); }
  };

  const fetchTaskGuide = async (taskId, platformKey) => {
    try {
      const [taskRes, contentRes] = await Promise.all([
        fetch(`${API_URL}/api/link-building/citation-automation/task/${taskId}`),
        fetch(`${API_URL}/api/link-building/citation-automation/content`)
      ]);
      if (taskRes.ok) {
        const data = await taskRes.json();
        setSelectedTask(data.task);
        setTaskGuide(data.guide);
      }
      if (contentRes.ok) {
        setSubmissionContent(await contentRes.json());
      }
    } catch (err) { console.error(err); }
  };

  const updateTaskStatus = async (taskId, status, profileUrl = null) => {
    try {
      await fetch(`${API_URL}/api/link-building/citation-automation/task/${taskId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, profile_url: profileUrl })
      });
      fetchCitationData();
      setSelectedTask(null);
      setTaskGuide(null);
    } catch (err) { console.error(err); }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  useEffect(() => {
    if (activeTab === 'citation-workflow') {
      (async () => {
        try {
          const res = await fetch(`${API_URL}/api/link-building/citation-automation/dashboard`);
          if (res.ok) {
            const data = await res.json();
            setCitationDashboard(data);
            setCitationTasks(data.all_tasks || []);
          }
        } catch (err) { console.error(err); }
      })();
    }
  }, [activeTab]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div></div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet><title>Link Building | Admin</title></Helmet>

      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-4">
        <h1 className="text-2xl font-bold">🔗 Local Link Building</h1>
        <p className="text-indigo-100 text-sm">Track backlinks and identify local partnership opportunities</p>
      </div>

      <div className="border-b border-gray-200 px-6 bg-white">
        <nav className="flex space-x-6">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition ${activeTab === tab.id ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="p-6">
        {activeTab === 'dashboard' && dashboard && (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-indigo-500">
                <p className="text-gray-500 text-sm">Total Backlinks</p>
                <p className="text-2xl font-bold">{dashboard.stats.total_backlinks}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
                <p className="text-gray-500 text-sm">Active</p>
                <p className="text-2xl font-bold text-green-600">{dashboard.stats.active_backlinks}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-purple-500">
                <p className="text-gray-500 text-sm">Avg DA</p>
                <p className="text-2xl font-bold text-purple-600">{dashboard.stats.average_da}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
                <p className="text-gray-500 text-sm">Opportunities</p>
                <p className="text-2xl font-bold text-blue-600">{dashboard.stats.total_opportunities}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-orange-500">
                <p className="text-gray-500 text-sm">Pending</p>
                <p className="text-2xl font-bold text-orange-600">{dashboard.stats.pending_opportunities}</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow">
                <div className="p-4 border-b"><h3 className="font-semibold">🎯 Top Opportunities</h3></div>
                <div className="divide-y">
                  {dashboard.top_opportunities?.slice(0, 5).map(o => (
                    <div key={o.id} className="p-4 flex justify-between items-center">
                      <div>
                        <span className="mr-2">{getCategoryIcon(o.category)}</span>
                        <span className="font-medium">{o.name}</span>
                        <span className="text-gray-500 text-sm ml-2">DA: {o.domain_authority || 'N/A'}</span>
                      </div>
                      {getPriorityBadge(o.priority)}
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-lg shadow">
                <div className="p-4 border-b"><h3 className="font-semibold">🔗 Recent Backlinks</h3></div>
                <div className="divide-y">
                  {dashboard.recent_backlinks?.slice(0, 5).map(b => (
                    <div key={b.id} className="p-4">
                      <a href={b.source_url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline text-sm">{b.source_domain}</a>
                      <p className="text-gray-500 text-xs">Anchor: {b.anchor_text || 'N/A'}</p>
                    </div>
                  ))}
                  {(!dashboard.recent_backlinks || dashboard.recent_backlinks.length === 0) && <div className="p-4 text-gray-500 text-sm">No backlinks tracked yet</div>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CITATION WORKFLOW TAB */}
        {activeTab === 'citation-workflow' && (
          <div>
            {/* Progress Overview */}
            {citationDashboard && (
              <div className="mb-6">
                <div className="bg-gradient-to-r from-green-50 to-teal-50 border border-green-200 rounded-lg p-4 mb-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-bold text-lg text-green-800">🚀 Citation Building Progress</h3>
                      <p className="text-green-700 text-sm">Automated workflow to build backlinks from {citationDashboard.progress.total} opportunities</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-green-600">{citationDashboard.progress.completion_percentage}%</p>
                      <p className="text-sm text-green-700">{citationDashboard.progress.completed}/{citationDashboard.progress.total} Complete</p>
                    </div>
                  </div>
                  <div className="mt-3 bg-green-200 rounded-full h-3 overflow-hidden">
                    <div 
                      className="bg-green-600 h-full transition-all duration-500" 
                      style={{ width: `${citationDashboard.progress.completion_percentage}%` }}
                    />
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white p-4 rounded-lg shadow border-l-4 border-red-500">
                    <p className="text-gray-500 text-sm">High DA (80+)</p>
                    <p className="text-2xl font-bold text-red-600">{citationDashboard.by_priority.high_da_80plus.total}</p>
                    <p className="text-xs text-gray-400">{citationDashboard.by_priority.high_da_80plus.completed} done</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow border-l-4 border-orange-500">
                    <p className="text-gray-500 text-sm">Medium DA (50-80)</p>
                    <p className="text-2xl font-bold text-orange-600">{citationDashboard.by_priority.medium_da_50_80.total}</p>
                    <p className="text-xs text-gray-400">{citationDashboard.by_priority.medium_da_50_80.completed} done</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
                    <p className="text-gray-500 text-sm">Total DA Potential</p>
                    <p className="text-2xl font-bold text-blue-600">{citationDashboard.da_impact.potential_da}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
                    <p className="text-gray-500 text-sm">DA Acquired</p>
                    <p className="text-2xl font-bold text-green-600">{citationDashboard.da_impact.acquired_da}</p>
                  </div>
                </div>

                {/* Today's Tasks */}
                {citationDashboard.todays_tasks.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                    <h4 className="font-semibold text-yellow-800 mb-2">📅 Today&apos;s Tasks ({citationDashboard.todays_tasks.length})</h4>
                    <div className="space-y-2">
                      {citationDashboard.todays_tasks.map(task => (
                        <div key={task.id} className="flex justify-between items-center bg-white p-3 rounded border">
                          <div>
                            <span className="font-medium">{task.platform}</span>
                            <span className="ml-2 text-sm text-gray-500">DA {task.da}</span>
                          </div>
                          <button 
                            onClick={() => fetchTaskGuide(task.id, task.platform_key)}
                            className="bg-yellow-600 text-white px-3 py-1 rounded text-sm hover:bg-yellow-700"
                          >
                            Start →
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Task Detail Modal */}
            {selectedTask && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-6 border-b bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-xl font-bold">{selectedTask.platform}</h3>
                        <p className="text-indigo-100">Domain Authority: {selectedTask.da} | {taskGuide?.time_estimate || '15-30 min'}</p>
                      </div>
                      <button onClick={() => { setSelectedTask(null); setTaskGuide(null); }} className="text-white hover:text-gray-200 text-2xl">&times;</button>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    {/* Step-by-step guide */}
                    {taskGuide && (
                      <div className="mb-6">
                        <h4 className="font-semibold mb-3">📋 Step-by-Step Guide</h4>
                        <div className="space-y-3">
                          {taskGuide.steps.map((step, idx) => (
                            <div key={idx} className="flex gap-3 p-3 bg-gray-50 rounded">
                              <span className="bg-indigo-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm flex-shrink-0">{step.step}</span>
                              <div>
                                <p>{step.action}</p>
                                {step.url && <a href={step.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm hover:underline">Open {step.url}</a>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Copy-paste content */}
                    {submissionContent && (
                      <div className="mb-6">
                        <h4 className="font-semibold mb-3">📝 Content to Copy</h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="p-3 bg-gray-50 rounded">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-sm font-medium text-gray-600">Business Name</span>
                              <button onClick={() => copyToClipboard(submissionContent.content.business_name, 'name')} className="text-blue-600 text-xs hover:underline">
                                {copied === 'name' ? '✓ Copied!' : 'Copy'}
                              </button>
                            </div>
                            <p className="font-medium">{submissionContent.content.business_name}</p>
                          </div>
                          <div className="p-3 bg-gray-50 rounded">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-sm font-medium text-gray-600">Phone</span>
                              <button onClick={() => copyToClipboard(submissionContent.content.phone, 'phone')} className="text-blue-600 text-xs hover:underline">
                                {copied === 'phone' ? '✓ Copied!' : 'Copy'}
                              </button>
                            </div>
                            <p className="font-medium">{submissionContent.content.phone}</p>
                          </div>
                          <div className="p-3 bg-gray-50 rounded">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-sm font-medium text-gray-600">Address</span>
                              <button onClick={() => copyToClipboard(submissionContent.content.address, 'address')} className="text-blue-600 text-xs hover:underline">
                                {copied === 'address' ? '✓ Copied!' : 'Copy'}
                              </button>
                            </div>
                            <p className="font-medium">{submissionContent.content.address}</p>
                          </div>
                          <div className="p-3 bg-gray-50 rounded">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-sm font-medium text-gray-600">Website (with tracking)</span>
                              <button onClick={() => copyToClipboard(submissionContent.formatted.website_with_tracking, 'website')} className="text-blue-600 text-xs hover:underline">
                                {copied === 'website' ? '✓ Copied!' : 'Copy'}
                              </button>
                            </div>
                            <p className="font-medium text-sm break-all">{submissionContent.formatted.website_with_tracking}</p>
                          </div>
                          <div className="p-3 bg-gray-50 rounded md:col-span-2">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-sm font-medium text-gray-600">Short Description</span>
                              <button onClick={() => copyToClipboard(submissionContent.content.short_description, 'desc')} className="text-blue-600 text-xs hover:underline">
                                {copied === 'desc' ? '✓ Copied!' : 'Copy'}
                              </button>
                            </div>
                            <p className="text-sm">{submissionContent.content.short_description}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-4 border-t">
                      <button 
                        onClick={() => updateTaskStatus(selectedTask.id, 'completed')}
                        className="flex-1 bg-green-600 text-white px-4 py-3 rounded hover:bg-green-700 font-medium"
                      >
                        ✓ Mark as Completed
                      </button>
                      <button 
                        onClick={() => updateTaskStatus(selectedTask.id, 'in_progress')}
                        className="flex-1 bg-yellow-600 text-white px-4 py-3 rounded hover:bg-yellow-700 font-medium"
                      >
                        ⏳ In Progress
                      </button>
                      <button 
                        onClick={() => updateTaskStatus(selectedTask.id, 'skipped')}
                        className="bg-gray-400 text-white px-4 py-3 rounded hover:bg-gray-500"
                      >
                        Skip
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* All Tasks List */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b flex justify-between items-center">
                <h4 className="font-semibold">All Citation Tasks (Sorted by DA)</h4>
                <div className="flex gap-2">
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">✓ Completed</span>
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded">⏳ In Progress</span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">Pending</span>
                </div>
              </div>
              <div className="divide-y max-h-[600px] overflow-y-auto">
                {citationTasks.map(task => (
                  <div key={task.id} className={`p-4 flex justify-between items-center hover:bg-gray-50 ${task.status === 'completed' ? 'bg-green-50' : task.status === 'in_progress' ? 'bg-yellow-50' : ''}`}>
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${task.da >= 80 ? 'bg-red-500' : task.da >= 50 ? 'bg-orange-500' : 'bg-gray-400'}`}>
                        {task.da}
                      </span>
                      <div>
                        <p className="font-medium">{task.platform}</p>
                        <p className="text-xs text-gray-500">Scheduled: {task.scheduled_date}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {task.status === 'completed' && <span className="text-green-600">✓ Done</span>}
                      {task.status === 'in_progress' && <span className="text-yellow-600">⏳</span>}
                      {task.status === 'pending' && (
                        <button 
                          onClick={() => fetchTaskGuide(task.id, task.platform_key)}
                          className="bg-indigo-600 text-white px-3 py-1 rounded text-sm hover:bg-indigo-700"
                        >
                          Start
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'opportunities' && (
          <div>
            <div className="mb-4 flex gap-2 flex-wrap">
              <button onClick={() => setSelectedCategory('all')} className={`px-3 py-1 rounded text-sm ${selectedCategory === 'all' ? 'bg-indigo-600 text-white' : 'bg-gray-200'}`}>All ({opportunities.length})</button>
              {categories.map(cat => (
                <button key={cat} onClick={() => setSelectedCategory(cat)} className={`px-3 py-1 rounded text-sm ${selectedCategory === cat ? 'bg-indigo-600 text-white' : 'bg-gray-200'}`}>
                  {getCategoryIcon(cat)} {cat}
                </button>
              ))}
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">DA</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredOpportunities.map(o => (
                    <tr key={o.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="font-medium">{o.name}</p>
                        <a href={o.url} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:underline">{o.url}</a>
                      </td>
                      <td className="px-4 py-3 text-sm">{getCategoryIcon(o.category)} {o.category}</td>
                      <td className="px-4 py-3 font-medium">{o.domain_authority || '-'}</td>
                      <td className="px-4 py-3">{getPriorityBadge(o.priority)}</td>
                      <td className="px-4 py-3">{getStatusBadge(o.status)}</td>
                      <td className="px-4 py-3">
                        <select value={o.status} onChange={e => updateOpportunityStatus(o.id, e.target.value)} className="text-sm border rounded px-2 py-1">
                          <option value="identified">Identified</option>
                          <option value="contacted">Contacted</option>
                          <option value="in-progress">In Progress</option>
                          <option value="acquired">Acquired</option>
                          <option value="rejected">Rejected</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'backlinks' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Anchor Text</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">DA</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {backlinks.map(b => (
                  <tr key={b.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <a href={b.source_url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">{b.source_domain}</a>
                    </td>
                    <td className="px-4 py-3 text-sm">{b.anchor_text || '-'}</td>
                    <td className="px-4 py-3 font-medium">{b.domain_authority || '-'}</td>
                    <td className="px-4 py-3 text-sm capitalize">{b.link_type}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs ${b.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{b.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {backlinks.length === 0 && <div className="p-8 text-center text-gray-500">No backlinks tracked yet. Add your first backlink!</div>}
          </div>
        )}

        {activeTab === 'add' && (
          <div className="max-w-xl bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold text-gray-800 mb-4">➕ Add New Backlink</h3>
            <div className="space-y-4">
              <input placeholder="Source URL (e.g., https://fortworthchamber.com/members/fort-worth-dental)" value={newBacklink.source_url} onChange={e => setNewBacklink({...newBacklink, source_url: e.target.value})} className="w-full border rounded px-3 py-2" />
              <input placeholder="Source Domain (e.g., fortworthchamber.com)" value={newBacklink.source_domain} onChange={e => setNewBacklink({...newBacklink, source_domain: e.target.value})} className="w-full border rounded px-3 py-2" />
              <input placeholder="Anchor Text" value={newBacklink.anchor_text} onChange={e => setNewBacklink({...newBacklink, anchor_text: e.target.value})} className="w-full border rounded px-3 py-2" />
              <input type="number" placeholder="Domain Authority (DA)" value={newBacklink.domain_authority} onChange={e => setNewBacklink({...newBacklink, domain_authority: e.target.value})} className="w-full border rounded px-3 py-2" />
              <select value={newBacklink.category} onChange={e => setNewBacklink({...newBacklink, category: e.target.value})} className="w-full border rounded px-3 py-2">
                <option value="chamber">Chamber of Commerce</option>
                <option value="local-news">Local News</option>
                <option value="dental-associations">Dental Associations</option>
                <option value="healthcare">Healthcare</option>
                <option value="sponsorship">Sponsorship</option>
                <option value="general">General</option>
              </select>
              <button onClick={addBacklink} disabled={!newBacklink.source_url} className="w-full bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 disabled:opacity-50">Add Backlink</button>
            </div>
          </div>
        )}

        {/* AUTO-OUTREACH TAB */}
        {activeTab === 'outreach' && (
          <div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-800">📧 Automated Outreach System</h3>
              <p className="text-blue-700 text-sm mt-1">Send templated outreach emails to opportunities with contact information. Each type (chamber, sponsorship, press, charity) has a customized template.</p>
            </div>

            <div className="flex justify-between items-center mb-4">
              <div>
                <span className="text-gray-600">{outreachOpps.filter(o => !o.outreach_sent).length} opportunities ready for outreach</span>
              </div>
              <button onClick={sendBulkOutreach} disabled={sendingOutreach.bulk} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50">
                {sendingOutreach.bulk ? 'Sending...' : '📨 Send to All Not Contacted'}
              </button>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Opportunity</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">DA</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {outreachOpps.map(o => (
                    <tr key={o.name} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="font-medium">{o.name}</p>
                        <a href={o.url} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:underline">{o.url}</a>
                      </td>
                      <td className="px-4 py-3 text-sm">{o.type}</td>
                      <td className="px-4 py-3 font-medium">{o.da}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{o.contact_email}</td>
                      <td className="px-4 py-3">
                        {o.outreach_sent 
                          ? <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-700">✓ Sent</span>
                          : <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-600">Not sent</span>
                        }
                      </td>
                      <td className="px-4 py-3">
                        <button 
                          onClick={() => sendOutreach(o.name)} 
                          disabled={sendingOutreach[o.name] || o.outreach_sent}
                          className="bg-blue-100 text-blue-700 px-3 py-1 rounded text-sm hover:bg-blue-200 disabled:opacity-50"
                        >
                          {sendingOutreach[o.name] ? 'Sending...' : o.outreach_sent ? 'Sent' : 'Send →'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* HEALTH MONITOR TAB */}
        {activeTab === 'health' && (
          <div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-green-800">🔍 Backlink Health Monitor</h3>
              <p className="text-green-700 text-sm mt-1">Automatically check if your acquired backlinks are still live and linking to your site.</p>
            </div>

            <div className="flex justify-end mb-4">
              <button onClick={checkAllHealth} disabled={checkingHealth} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50">
                {checkingHealth ? '⏳ Checking...' : '🔄 Check All Backlinks'}
              </button>
            </div>

            {healthReport && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-white p-4 rounded-lg shadow text-center">
                    <p className="text-gray-500 text-sm">Total</p>
                    <p className="text-2xl font-bold">{healthReport.total_backlinks}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow text-center border-l-4 border-green-500">
                    <p className="text-gray-500 text-sm">Active</p>
                    <p className="text-2xl font-bold text-green-600">{healthReport.by_status?.active || 0}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow text-center border-l-4 border-red-500">
                    <p className="text-gray-500 text-sm">Lost</p>
                    <p className="text-2xl font-bold text-red-600">{healthReport.by_status?.lost || 0}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow text-center border-l-4 border-yellow-500">
                    <p className="text-gray-500 text-sm">Pending</p>
                    <p className="text-2xl font-bold text-yellow-600">{healthReport.by_status?.pending || 0}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow text-center border-l-4 border-blue-500">
                    <p className="text-gray-500 text-sm">Health Score</p>
                    <p className="text-2xl font-bold text-blue-600">{healthReport.health_score}%</p>
                  </div>
                </div>

                {healthReport.alerts?.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="font-semibold text-red-800 mb-2">⚠️ Alerts</h4>
                    {healthReport.alerts.map(a => (
                      <div key={a.id} className="flex justify-between items-center py-2 border-b border-red-100 last:border-0">
                        <span className="text-red-700">{a.count} backlink(s) lost: {a.domains?.join(', ')}</span>
                        <span className="text-red-500 text-xs">{new Date(a.created_at).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                )}

                {healthReport.recently_lost?.length > 0 && (
                  <div className="bg-white rounded-lg shadow">
                    <div className="p-4 border-b"><h4 className="font-semibold">Recently Lost Backlinks</h4></div>
                    <div className="divide-y">
                      {healthReport.recently_lost.map(b => (
                        <div key={b.id} className="p-4 flex justify-between items-center">
                          <div>
                            <span className="font-medium">{b.source_domain}</span>
                            <p className="text-xs text-gray-500">{b.source_url}</p>
                          </div>
                          <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-700">Lost</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {healthReport.total_backlinks === 0 && (
                  <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                    No backlinks to monitor yet. Add backlinks first!
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* PR → NEWS INTEGRATION TAB */}
        {activeTab === 'pr-integration' && (
          <div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-purple-800">📰 Press Release → News Outlet Integration</h3>
              <p className="text-purple-700 text-sm mt-1">Automatically send your published press releases to local news outlets via email.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Select PR */}
              <div className="bg-white rounded-lg shadow p-4">
                <h4 className="font-semibold mb-3">1. Select Press Release</h4>
                <select 
                  value={selectedPR} 
                  onChange={e => setSelectedPR(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">-- Select a published press release --</option>
                  {pressReleases.map(pr => (
                    <option key={pr.id} value={pr.id}>{pr.title}</option>
                  ))}
                </select>
                {pressReleases.length === 0 && (
                  <p className="text-gray-500 text-sm mt-2">No published press releases. Create one in the Press Releases section first.</p>
                )}
              </div>

              {/* Select Outlets */}
              <div className="bg-white rounded-lg shadow p-4">
                <h4 className="font-semibold mb-3">2. Select News Outlets ({selectedOutlets.length} selected)</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {prOutlets.map(o => (
                    <label key={o.name} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={selectedOutlets.includes(o.name)}
                        onChange={() => toggleOutlet(o.name)}
                        className="rounded"
                      />
                      <span className="flex-1">{o.name}</span>
                      <span className="text-xs text-gray-500">DA: {o.da}</span>
                    </label>
                  ))}
                </div>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => setSelectedOutlets(prOutlets.map(o => o.name))} className="text-sm text-blue-600 hover:underline">Select All</button>
                  <button onClick={() => setSelectedOutlets([])} className="text-sm text-gray-600 hover:underline">Clear</button>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <button 
                onClick={sendPRToOutlets} 
                disabled={sendingPR || !selectedPR || selectedOutlets.length === 0}
                className="w-full bg-purple-600 text-white px-4 py-3 rounded hover:bg-purple-700 disabled:opacity-50"
              >
                {sendingPR ? '📤 Sending...' : `📧 Send PR to ${selectedOutlets.length} Outlet(s)`}
              </button>
            </div>

            <div className="mt-6 bg-white rounded-lg shadow">
              <div className="p-4 border-b"><h4 className="font-semibold">News Outlets That Accept Press Releases</h4></div>
              <div className="divide-y">
                {prOutlets.map(o => (
                  <div key={o.name} className="p-4 flex justify-between items-center">
                    <div>
                      <span className="font-medium">{o.name}</span>
                      <span className="text-xs text-gray-500 ml-2">DA: {o.da}</span>
                    </div>
                    <span className="text-sm text-gray-600">{o.contact_email}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LinkBuilding;
